// eslint-disable-next-line import/no-cycle
import { filesApi } from '../includes/filesApi';

export default {
  /**
   * Upload a profile picture
   * @param {string} base64Image - Base64 encoded image data
   * @returns {Promise<Object>} - Response with picture_profile URLs
   * @description Uploads a profile picture and returns URLs for small, medium, and large versions
   * @throws {Error} - If the upload fails
   */
  async uploadProfilePicture(base64Image) {
    try {
      const response = await filesApi.post('/files/upload-profile-picture', {
        base64Image,
      });

      if (response.data.success) {
        return response.data.picture_profile;
      }
      throw new Error(`Upload failed: ${response.data.message || 'Unknown error'}`);
    } catch (error) {
      console.error('Profile picture upload failed:', error);

      // Handle specific HTTP errors
      if (error.response?.status === 413) {
        throw new Error('File too large. Please try a smaller image or contact support.');
      } else if (error.response?.status === 401) {
        throw new Error('Authentication required. Please log in again.');
      } else if (error.response?.status >= 500) {
        throw new Error('Server error. Please try again later.');
      }

      throw error;
    }
  },

  /**
   * Get profile picture URL for a specific size
   * @param {string} filename - The filename from the picture_profile object
   * @returns {string} - Full URL to the profile picture
   * @description Constructs the full URL for a profile picture based on filename
   */
  getProfilePictureUrl(filename) {
    if (!filename) return null;

    // Use relative path instead of environment variable
    return `/files-api/files/userimages/${filename}`;
  },

  /**
   * Download a file by its ID
   * @param {string} fileId - The file ID from the database
   * @returns {Promise<Blob>} - File blob
   * @description Downloads a file by its database ID (requires authentication)
   * @throws {Error} - If the download fails
   */
  async downloadFile(fileId) {
    try {
      const response = await filesApi.get(`/files/download/${fileId}`, {
        responseType: 'blob',
      });

      return response.data;
    } catch (error) {
      console.error('File download failed:', error);
      throw error;
    }
  },

  /**
   * Convert file to base64
   * @param {File} file - File object from input
   * @returns {Promise<string>} - Base64 encoded string
   * @description Converts a file to base64 format for upload
   */
  async fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  },

  /**
   * Validate image file
   * @param {File} file - File object to validate
   * @param {Object} options - Validation options
   * @param {number} options.maxSize - Maximum file size in MB (default: 5)
   * @param {Array<string>} options.allowedTypes - Allowed MIME types (default: image types)
   * @returns {Object} - Validation result { valid: boolean, error?: string }
   * @description Validates an image file before upload
   */
  validateImageFile(file, options = {}) {
    const {
      maxSize = 5, // 5MB default
      allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'],
    } = options;

    // Check file size
    const maxSizeBytes = maxSize * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      return {
        valid: false,
        error: `File size must be less than ${maxSize}MB`,
      };
    }

    // Check file type
    if (!allowedTypes.includes(file.type)) {
      return {
        valid: false,
        error: `File type not supported. Allowed types: ${allowedTypes.join(', ')}`,
      };
    }

    return { valid: true };
  },

  /**
   * Resize image before upload (client-side)
   * @param {File} file - File object to resize
   * @param {Object} options - Resize options
   * @param {number} options.maxWidth - Maximum width (default: 1920)
   * @param {number} options.maxHeight - Maximum height (default: 1080)
   * @param {number} options.quality - JPEG quality 0-1 (default: 0.8)
   * @returns {Promise<File>} - Resized file
   * @description Resizes an image on the client side before upload
   */
  async resizeImage(file, options = {}) {
    const { maxWidth = 1920, maxHeight = 1080, quality = 0.8 } = options;

    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        // Calculate new dimensions
        let { width, height } = img;

        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }

        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }

        // Set canvas dimensions
        canvas.width = width;
        canvas.height = height;

        // Draw resized image
        ctx.drawImage(img, 0, 0, width, height);

        // Convert to blob with compression
        canvas.toBlob(
          (blob) => {
            const resizedFile = new File([blob], file.name, {
              type: file.type,
              lastModified: Date.now(),
            });
            resolve(resizedFile);
          },
          file.type,
          quality,
        );
      };

      img.onerror = reject;
      img.src = URL.createObjectURL(file);
    });
  },

  /**
   * Compress image to reduce file size
   * @param {File} file - File object to compress
   * @param {Object} options - Compression options
   * @param {number} options.maxSizeMB - Maximum file size in MB (default: 2)
   * @param {number} options.quality - JPEG quality 0-1 (default: 0.7)
   * @returns {Promise<File>} - Compressed file
   * @description Compresses an image to reduce file size before upload
   */
  async compressImage(file, options = {}) {
    const { maxSizeMB = 2, quality = 0.7 } = options;
    const maxSizeBytes = maxSizeMB * 1024 * 1024;

    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        // Start with original dimensions
        const { width, height } = img;
        let currentQuality = quality;

        const compress = () => {
          // Set canvas dimensions
          canvas.width = width;
          canvas.height = height;

          // Draw image
          ctx.drawImage(img, 0, 0, width, height);

          // Convert to blob
          canvas.toBlob(
            (blob) => {
              if (blob.size <= maxSizeBytes || currentQuality <= 0.1) {
                const compressedFile = new File([blob], file.name, {
                  type: file.type,
                  lastModified: Date.now(),
                });
                resolve(compressedFile);
              } else {
                // Reduce quality and try again
                currentQuality -= 0.1;
                compress();
              }
            },
            file.type,
            currentQuality,
          );
        };

        compress();
      };

      img.onerror = reject;
      img.src = URL.createObjectURL(file);
    });
  },

  /**
   * Upload profile picture with validation and resizing
   * @param {File} file - File object to upload
   * @param {Object} options - Upload options
   * @returns {Promise<Object>} - Response with picture_profile URLs
   * @description Complete profile picture upload with validation and optional resizing
   */
  async uploadProfilePictureWithValidation(file, options = {}) {
    const {
      validate = true,
      resize = true,
      compress = true,
      maxSize = 5,
      maxWidth = 1920,
      maxHeight = 1080,
      quality = 0.8,
      maxCompressedSize = 2,
    } = options;

    try {
      // Validate file
      if (validate) {
        const validation = this.validateImageFile(file, { maxSize });
        if (!validation.valid) {
          throw new Error(validation.error);
        }
      }

      // Process image
      let processedFile = file;

      // Resize image if requested
      if (resize) {
        processedFile = await this.resizeImage(file, { maxWidth, maxHeight, quality });
      }

      // Compress image if requested and still too large
      if (compress && processedFile.size > maxCompressedSize * 1024 * 1024) {
        processedFile = await this.compressImage(processedFile, {
          maxSizeMB: maxCompressedSize,
          quality,
        });
      }

      // Convert to base64
      const base64Image = await this.fileToBase64(processedFile);

      // Check base64 size (rough estimate: base64 is ~33% larger than binary)
      const estimatedSizeMB = (base64Image.length * 0.75) / (1024 * 1024);
      if (estimatedSizeMB > 45) {
        // Leave some buffer for 50MB limit
        throw new Error(
          `File too large after processing (${estimatedSizeMB.toFixed(
            1,
          )}MB). Please try a smaller image.`,
        );
      }

      // Upload to server
      return await this.uploadProfilePicture(base64Image);
    } catch (error) {
      console.error('Profile picture upload with validation failed:', error);

      // Handle specific error types
      if (error.response?.status === 413) {
        throw new Error('File too large. Please try a smaller image or contact support.');
      } else if (error.response?.status === 401) {
        throw new Error('Authentication required. Please log in again.');
      } else if (error.response?.status >= 500) {
        throw new Error('Server error. Please try again later.');
      }

      throw error;
    }
  },

  /**
   * Get user's current profile picture URLs
   * @param {Object} user - User object with picture_profile
   * @returns {Object} - Object with small, medium, large URLs
   * @description Extracts profile picture URLs from user object
   */
  getUserProfilePictures(user) {
    if (!user || !user.picture_profile) {
      return {
        small: null,
        medium: null,
        large: null,
      };
    }

    return {
      small: user.picture_profile.small,
      medium: user.picture_profile.medium,
      large: user.picture_profile.large,
    };
  },

  /**
   * Check if profile picture URLs are local (new API)
   * @param {Object} pictureProfile - Picture profile object
   * @returns {boolean} - True if URLs are local
   * @description Determines if profile pictures are using the new local API
   */
  isLocalProfilePicture(pictureProfile) {
    if (!pictureProfile) return false;

    // Use relative path instead of environment variable
    const localPrefix = '/files-api/files/userimages/';

    return [pictureProfile.small, pictureProfile.medium, pictureProfile.large].every(
      (url) => url && url.startsWith(localPrefix),
    );
  },

  async listContentImages() {
    const response = await filesApi.get('/files/content-images');
    return response.data.images || [];
  },

  async uploadContentImage(file, overwrite = false) {
    const formData = new FormData();
    formData.append('file', file);
    const url = overwrite
      ? '/files/upload-content-image?overwrite=true'
      : '/files/upload-content-image';
    const response = await filesApi.post(url, formData);
    return response.data;
  },

  async deleteContentImage(filename) {
    const response = await filesApi.delete(`/files/content-images/${encodeURIComponent(filename)}`);
    return response.data;
  },
};
